import {useState} from 'react';
import {useNavigate} from 'react-router-dom';

import {
  Badge,
  Button,
  MinusIcon,
  PlusIcon,
  Table,
  TBody,
  Td,
  Th,
  Thead,
  Tr,
} from '@/components';
import type {BusinessLiveAccessListItem} from '@/types';
import * as formatDate from '@/utils/date';

import {liveAccessStatusColorScheme} from './liveAccessStatus';
import LiveAccessSummary from './LiveAccessSummary';

const LiveAccessTable = ({
  items,
  onChanged,
}: {
  items: BusinessLiveAccessListItem[];
  onChanged: () => void;
}) => {
  return (
    <Table className="overflow-hidden" containerDataCy="live-access-table">
      <Thead>
        <Tr enableHover={false}>
          <Th className="w-12 px-4">&nbsp;</Th>
          <Th className="px-4">Business</Th>
          <Th className="px-4">Owner</Th>
          <Th className="px-4">Status</Th>
          <Th className="px-4">Requested</Th>
          <Th className="px-4">Action</Th>
        </Tr>
      </Thead>
      <TBody>
        {items.map((item, index) => (
          <LiveAccessRow
            key={item.id ?? index}
            item={item}
            index={index}
            onChanged={onChanged}
          />
        ))}
      </TBody>
    </Table>
  );
};

const LiveAccessRow = ({
  item,
  index,
  onChanged,
}: {
  item: BusinessLiveAccessListItem;
  index: number;
  onChanged: () => void;
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const navigate = useNavigate();
  const zebraRow = index % 2 === 0 ? '' : 'bg-gray-50 dark:bg-dark-zebraRow';

  return (
    <>
      <Tr
        className={zebraRow}
        data-cy="live-access-row"
        data-request-id={item.id}
      >
        <Td isNumeric className="px-4">
          <Button
            onClick={() => setIsExpanded(!isExpanded)}
            variant="unStyled"
            dataCy="live-access-row-expand"
          >
            <div
              className={`border-2 border-gray-700 dark:border-dark-borderLight dark:text-gray-100 text-gray-700 w-5 h-5 flex justify-center rounded hover:bg-cyan-50 ${
                isExpanded ? 'bg-cyan-50' : ''
              }`}
            >
              {isExpanded ? (
                <MinusIcon className="w-4 h-4" />
              ) : (
                <PlusIcon className="w-4 h-4" />
              )}
            </div>
          </Button>
        </Td>
        <Td className="font-medium px-4">{item.businessName || '—'}</Td>
        <Td className="px-4">{item.ownerEmail || '—'}</Td>
        <Td className="px-4">
          <Badge
            label={item.status}
            colorScheme={liveAccessStatusColorScheme(item.status)}
            dataCy="live-access-row-status"
          />
        </Td>
        <Td className="px-4">{formatDate.DDMonYYYY(item.createdAt || '')}</Td>
        <Td className="px-4">
          <Button
            variant="outline"
            colorScheme="cyan"
            className="!py-1 !px-2"
            dataCy="live-access-row-view-logs"
            onClick={() =>
              navigate(
                `/merchants/businesses/${item.businessId}/live-access/${item.id}/logs`,
              )
            }
          >
            <span className="text-xs">View Logs</span>
          </Button>
        </Td>
      </Tr>

      {isExpanded && (
        <Tr enableHover={false}>
          <Td colspan={6} className="p-0 m-0">
            <div className="flex py-5 px-8 justify-center bg-sky-50 dark:bg-transparent w-full">
              <LiveAccessSummary item={item} onChanged={onChanged} />
            </div>
          </Td>
        </Tr>
      )}
    </>
  );
};

export default LiveAccessTable;
